// LLM Chat API Service
import { getBackendUrl } from '../utils/api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ChatRequest {
  client_name?: string;
  case_name: string;
  user_message: string;
  conversation_history: ChatMessage[];
}

export interface ChatResponse {
  response: string;
  context_loaded: boolean;
  sources_used: Array<{
    filename: string;
    content_type: string;
    relevance_score: number;
  }>;
  package_info?: {
    client_name: string;
    case_name: string;
    domains_analyzed: string[];
    files_available: string[] | number;
  };
}

export class LLMChatService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || `${getBackendUrl()}/api`;
  }

  /**
   * Send a chat message to the LLM endpoint
   */
  async sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      console.log(`🔄 Calling LLM API at: ${this.baseUrl}/llm-chat`);
      console.log(`📋 Request payload:`, { 
        client_name: request.client_name, 
        case_name: request.case_name,
        user_message: request.user_message.substring(0, 100) + '...',
        history_length: request.conversation_history.length 
      });

      const response = await fetch(`${this.baseUrl}/llm-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result: ChatResponse = await response.json();
      console.log(`✅ LLM API Response received:`, {
        response_length: result.response.length,
        context_loaded: result.context_loaded,
        sources_count: result.sources_used.length,
        has_package_info: !!result.package_info
      });
      
      return result;

    } catch (error) {
      console.error('❌ LLM Chat API Error:', error);
      
      // Provide better error context
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to backend server at ${this.baseUrl}. Is your backend running on port 8002?`);
      }
      
      throw error;
    }
  }

  /**
   * Get available evidence packages for client/case selection
   */
  async getAvailablePackages(): Promise<Array<{
    client_name: string;
    case_name: string;
    domain: string;
    package_id: string;
    original_url: string;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/llm-packages`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const result = await response.json();
      return result.packages || [];

    } catch (error) {
      console.error('Error fetching available packages:', error);
      throw error;
    }
  }

  /**
   * Convert frontend message format to API format
   */
  convertToApiHistory(messages: Array<{
    type: 'user' | 'assistant' | 'system' | 'error';
    content: string;
    timestamp: Date;
  }>): ChatMessage[] {
    return messages
      .filter(msg => msg.type === 'user' || msg.type === 'assistant')
      .map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.timestamp.toISOString()
      }));
  }
}

// Create singleton instance pointing to backend
export const llmChatService = new LLMChatService(); 